import React, { useEffect, useState, useCallback } from "react";
import Navbar from "@/components/Navbar";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { motion } from "framer-motion";
import { Download, Loader2, Receipt, Euro, CheckCircle2, ShieldCheck, Inbox, FileText, Save, RotateCcw, BookOpen } from "lucide-react";

const MONTH_LABELS = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
const fmtMonth = (ym) => {
  if (!ym) return "Tutti i mesi";
  const [y, m] = ym.split("-");
  return `${MONTH_LABELS[parseInt(m, 10) - 1]} ${y}`;
};
const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const STATUS_STYLE = {
  paid: "bg-emerald-500/15 text-emerald-500",
  pending: "bg-amber-500/15 text-amber-500",
  failed: "bg-destructive/15 text-destructive",
  expired: "bg-muted text-muted-foreground",
  refunded: "bg-purple-500/15 text-purple-500",
  partially_refunded: "bg-purple-500/15 text-purple-500",
};
const STATUS_LABEL = {
  paid: "pagato", pending: "in attesa", failed: "fallito", expired: "scaduto",
  refunded: "rimborsato", partially_refunded: "rimb. parziale",
};

export default function Admin() {
  const [data, setData] = useState(null);
  const [month, setMonth] = useState("all");
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [refunding, setRefunding] = useState(null);
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);

  const loadSettings = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/settings");
      setSettings(data);
    } catch {
      toast.error("Errore nel caricamento delle impostazioni");
    }
  }, []);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const setField = (k, v) => setSettings((s) => ({ ...s, [k]: v }));

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const { data } = await api.put("/admin/settings", {
        document_mode: settings.document_mode,
        seller_name: settings.seller_name,
        seller_info: settings.seller_info,
        seller_vat: settings.seller_vat,
        seller_tax_code: settings.seller_tax_code,
        seller_address: settings.seller_address,
        vat_rate: settings.vat_rate === "" ? 0 : Number(settings.vat_rate),
        invoice_prefix: settings.invoice_prefix,
      });
      setSettings(data);
      toast.success("Impostazioni documento salvate");
    } catch {
      toast.error("Errore durante il salvataggio");
    } finally {
      setSavingSettings(false);
    }
  };

  const load = useCallback(async (m) => {
    setLoading(true);
    try {
      const params = m && m !== "all" ? { month: m } : {};
      const { data } = await api.get("/admin/transactions", { params });
      setData(data);
    } catch {
      toast.error("Errore nel caricamento delle transazioni");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(month); }, [month, load]);

  const refund = async (t) => {
    if (!window.confirm(`Rimborsare € ${t.amount.toFixed(2)} a ${t.email}? L'azione è definitiva e le battute accreditate verranno scalate.`)) return;
    setRefunding(t.id);
    try {
      const { data } = await api.post(`/admin/transactions/${t.id}/refund`, {});
      toast.success(`Rimborsato € ${data.refund_amount.toFixed(2)} · ${data.chars_deducted.toLocaleString("it-IT")} battute scalate`);
      load(month);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore durante il rimborso");
    } finally {
      setRefunding(null);
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = month && month !== "all" ? { month } : {};
      const res = await api.get("/admin/transactions/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `transazioni_${month === "all" ? "tutte" : month}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV esportato");
    } catch {
      toast.error("Errore durante l'esportazione");
    } finally {
      setExporting(false);
    }
  };

  const summary = data?.summary;
  const txs = data?.transactions || [];

  const renderBody = () => {
    if (loading) {
      return <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-brand-blue" /></div>;
    }
    if (txs.length === 0) {
      return (
        <div className="text-center py-24">
          <Inbox className="h-12 w-12 mx-auto text-muted-foreground/40 mb-4" />
          <p className="text-muted-foreground">Nessuna transazione per il periodo selezionato.</p>
        </div>
      );
    }
    return (
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="transactions-table">
          <thead>
            <tr className="text-left text-xs font-mono-label uppercase tracking-wider text-muted-foreground border-b border-border">
              <th className="px-5 py-4 font-medium">Data</th>
              <th className="px-5 py-4 font-medium">Email utente</th>
              <th className="px-5 py-4 font-medium">Pacchetto</th>
              <th className="px-5 py-4 font-medium text-right">Importo</th>
              <th className="px-5 py-4 font-medium">Stato</th>
              <th className="px-5 py-4 font-medium text-right">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {txs.map((t, i) => (
              <motion.tr key={t.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.02 }}
                data-testid={`tx-row-${t.id}`}
                className="border-b border-border/50 last:border-0 hover:bg-secondary/40 transition-colors">
                <td className="px-5 py-4 whitespace-nowrap tabular-nums">{fmtDate(t.date)}</td>
                <td className="px-5 py-4">{t.email}</td>
                <td className="px-5 py-4 text-muted-foreground">{t.plan}</td>
                <td className="px-5 py-4 text-right font-medium tabular-nums">€ {t.amount.toFixed(2)}</td>
                <td className="px-5 py-4">
                  <span className={`text-xs px-2.5 py-1 rounded-full ${STATUS_STYLE[t.status] || STATUS_STYLE.pending}`}>{STATUS_LABEL[t.status] || t.status}</span>
                </td>
                <td className="px-5 py-4 text-right">
                  {t.status === "paid" && !t.refunded ? (
                    <Button data-testid={`refund-btn-${t.id}`} onClick={() => refund(t)} disabled={refunding === t.id}
                      size="sm" variant="outline" className="rounded-full gap-1.5 h-8 text-destructive border-destructive/30 hover:bg-destructive/10">
                      {refunding === t.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
                      Rimborsa
                    </Button>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-[1200px] mx-auto px-4 md:px-8 py-8">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2 text-xs font-mono-label uppercase tracking-wider text-brand-pink mb-1">
              <ShieldCheck className="h-3.5 w-3.5" /> Area amministrativa
            </div>
            <h1 className="font-heading text-3xl font-light tracking-tight">Transazioni</h1>
            <p className="text-sm text-muted-foreground mt-1">Cronologia dei pagamenti per la contabilità fiscale.</p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger data-testid="month-select" className="w-[180px] rounded-full">
                <SelectValue placeholder="Mese" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="month-opt-all">Tutti i mesi</SelectItem>
                {(data?.available_months || []).map((m) => (
                  <SelectItem key={m} value={m} data-testid={`month-opt-${m}`}>{fmtMonth(m)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button data-testid="export-csv-btn" onClick={exportCsv} disabled={exporting || txs.length === 0}
              className="rounded-full bg-brand-blue hover:bg-brand-blue/90 text-white gap-2">
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              Esporta CSV
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <div className="rounded-2xl border border-border bg-card p-5" data-testid="stat-total">
            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-2"><Euro className="h-4 w-4 text-brand-pink" /> Incassato (pagati)</div>
            <p className="font-heading text-3xl font-semibold">€ {summary ? summary.total_paid.toFixed(2) : "0.00"}</p>
          </div>
          <div className="rounded-2xl border border-border bg-card p-5" data-testid="stat-paid">
            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-2"><CheckCircle2 className="h-4 w-4 text-emerald-500" /> Pagamenti riusciti</div>
            <p className="font-heading text-3xl font-semibold">{summary?.paid_count ?? 0}</p>
          </div>
          <div className="rounded-2xl border border-border bg-card p-5" data-testid="stat-count">
            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-2"><Receipt className="h-4 w-4 text-brand-blue" /> Transazioni totali</div>
            <p className="font-heading text-3xl font-semibold">{summary?.count ?? 0}</p>
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-card overflow-hidden">
          {renderBody()}
        </div>

        <div className="mt-8 rounded-2xl border border-border bg-card p-6" data-testid="billing-settings-card">
          <div className="flex items-center gap-2 mb-1">
            <FileText className="h-4 w-4 text-brand-pink" />
            <h2 className="font-heading text-xl font-medium">Impostazioni documento</h2>
          </div>
          <p className="text-sm text-muted-foreground mb-6">
            Scegli il tipo di documento generato per i clienti. Passa a "Fattura" quando avrai la partita IVA.
          </p>

          {!settings ? (
            <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-brand-blue" /></div>
          ) : (
            <div className="space-y-5 max-w-2xl">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-mono-label uppercase tracking-wider text-muted-foreground">Tipo documento</label>
                  <Select value={settings.document_mode} onValueChange={(v) => setField("document_mode", v)}>
                    <SelectTrigger data-testid="document-mode-select" className="rounded-full"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="receipt" data-testid="doc-mode-receipt">Ricevuta (senza P. IVA)</SelectItem>
                      <SelectItem value="invoice" data-testid="doc-mode-invoice">Fattura (con P. IVA)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-mono-label uppercase tracking-wider text-muted-foreground">Ragione sociale</label>
                  <Input data-testid="setting-seller-name" value={settings.seller_name || ""} onChange={(e) => setField("seller_name", e.target.value)} className="rounded-full" />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-mono-label uppercase tracking-wider text-muted-foreground">Descrizione venditore</label>
                <Input data-testid="setting-seller-info" value={settings.seller_info || ""} onChange={(e) => setField("seller_info", e.target.value)} className="rounded-full" />
              </div>

              {settings.document_mode === "invoice" && (
                <div className="space-y-5 rounded-xl border border-brand-pink/30 bg-brand-pink/5 p-4" data-testid="invoice-fields">
                  <p className="text-xs text-brand-pink font-medium">Dati fiscali obbligatori per la fattura</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-xs font-mono-label uppercase tracking-wider text-muted-foreground">Partita IVA</label>
                      <Input data-testid="setting-vat" value={settings.seller_vat || ""} onChange={(e) => setField("seller_vat", e.target.value)} className="rounded-full" placeholder="IT01234567890" />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-mono-label uppercase tracking-wider text-muted-foreground">Codice Fiscale</label>
                      <Input data-testid="setting-tax-code" value={settings.seller_tax_code || ""} onChange={(e) => setField("seller_tax_code", e.target.value)} className="rounded-full" />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-mono-label uppercase tracking-wider text-muted-foreground">Indirizzo</label>
                    <Input data-testid="setting-address" value={settings.seller_address || ""} onChange={(e) => setField("seller_address", e.target.value)} className="rounded-full" placeholder="Via, città, CAP" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-xs font-mono-label uppercase tracking-wider text-muted-foreground">Aliquota IVA (%)</label>
                      <Input data-testid="setting-vat-rate" type="number" value={settings.vat_rate ?? 22} onChange={(e) => setField("vat_rate", e.target.value)} className="rounded-full" />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-mono-label uppercase tracking-wider text-muted-foreground">Prefisso numero</label>
                      <Input data-testid="setting-prefix" value={settings.invoice_prefix || ""} onChange={(e) => setField("invoice_prefix", e.target.value)} className="rounded-full" />
                    </div>
                  </div>
                </div>
              )}

              <Button data-testid="save-settings-btn" onClick={saveSettings} disabled={savingSettings}
                className="rounded-full bg-brand-pink hover:bg-brand-pink/90 text-white gap-2">
                {savingSettings ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Salva impostazioni
              </Button>
            </div>
          )}
        </div>

        <div className="mt-8 rounded-2xl border border-border bg-card p-6" data-testid="admin-guide-card">
          <div className="flex items-center gap-2 mb-1">
            <BookOpen className="h-4 w-4 text-brand-blue" />
            <h2 className="font-heading text-xl font-medium">Guida rapida gestione</h2>
          </div>
          <p className="text-sm text-muted-foreground mb-6">Come gestire FreedWords e i servizi collegati.</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { t: "🖥️ App Admin (questa pagina)", d: "Transazioni, export CSV a fine mese, rimborsi, impostazioni Ricevuta/Fattura.", c: "text-brand-pink" },
              { t: "💳 Stripe · dashboard.stripe.com", d: "I soldi: incassi, payout su IBAN, abbonamenti, contestazioni. I rimborsi puoi farli anche da qui.", c: "text-brand-blue" },
              { t: "📧 Resend · resend.com", d: "Le email: log invii ricevute/fatture (consegnata/rimbalzata). Dominio freedwords.it verificato.", c: "text-emerald-500" },
              { t: "🚀 Emergent", d: "Il motore: redeploy dopo ogni modifica, crediti Universal Key (Profilo → Manage plan), log.", c: "text-amber-500" },
              { t: "🌐 Aruba (DNS)", d: "Solo per record DNS del dominio o casella email @freedwords.it.", c: "text-muted-foreground" },
              { t: "↩️ Come rimborsare", d: "Qui: pulsante 'Rimborsa' sulla riga del pagamento (scala anche le battute). Su Stripe: Pagamenti → apri → '...' → Rimborsa.", c: "text-destructive" },
            ].map((x) => (
              <div key={x.t} className="rounded-xl border border-border bg-background/50 p-4">
                <p className={`text-sm font-medium ${x.c}`}>{x.t}</p>
                <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">{x.d}</p>
              </div>
            ))}
          </div>

          <div className="mt-5 rounded-xl border border-brand-blue/25 bg-brand-blue/5 p-4">
            <p className="text-xs text-muted-foreground leading-relaxed">
              <strong className="text-foreground">Routine consigliata:</strong> controlla i pagamenti ogni settimana (qui + Stripe) · verifica le email su Resend se un cliente segnala · esporta il CSV a fine mese · fai un <strong>redeploy</strong> dopo ogni modifica dell'app.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
